import { useMemo, useRef } from "react";

import { css } from "@compiled/react";

import { media } from "@atlaskit/primitives/responsive";
import UFOSegment from "@atlaskit/react-ufo/segment";

import { wacToken } from "@/libraries/design/foundations";
import { ProductKeyTypes } from "@/libraries/wpl/foundations/src";
import { MobileNavLinksContainerInfo } from "@/patterns/global-nav/src/mobile-nav/mobile-nav-links-container";

import GlobalNavLinksContainer, {
    GlobalNavLinkContentProps,
    GlobalNavProvider
} from "../../global-nav/src/nav-links-container";

import { MobileSubNav } from "./subnav-mobile";

const navigationBarWrapperCss = css({
    borderBlockStart: `1px solid ${wacToken("color.neutral.light300")}`,
    alignItems: "center",
    background: wacToken("color.neutral.light0"),
    display: "flex",
    height: "72px",
    flexDirection: "row",
    justifyContent: "center",
    zIndex: 1,
    [media.below.md]: {
        display: "none"
    }
});

// @ts-ignore
const navBarShadow = css({
    height: "72px",
    // eslint-disable-next-line local/ensure-wac-token-usage
    boxShadow: "0 2px 10px rgba(0, 0, 0, .25)"
});

const navigationBarLogoCss = css({
    display: "flex",
    alignItems: "center",
    [media.above.md]: {
        width: "auto"
    }
});

const textWrapperCss = css({
    height: "100%",
    color: wacToken("color.primary.black"),
    alignItems: "center",
    display: "flex",
    flexDirection: "row"
});

const textCss = css({
    fontSize: "1.25rem",
    fontWeight: 600
});

// @ts-ignore
const ffTextCss = css({
    fontSize: "1.5rem",
    fontFamily: `"Charlie Display", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Noto Sans", Ubuntu, "Droid Sans", "Helvetica Neue", sans-serif`,
    fontWeight: 500,
    // eslint-disable-next-line local/ensure-wac-token-usage
    color: "#253858",
    lineHeight: 1
});

const mobileNavCss = css({
    display: "none",
    [media.below.md]: {
        display: "block"
    }
});

const subNavCss = css({
    position: "sticky",
    zIndex: "1",
    insetBlockStart: "56px",
    [media.above.sm]: {
        insetBlockStart: "52px"
    },
    [media.above.md]: {
        insetBlockStart: "68px"
    }
});

export type SubNavNavigationBarProps = {
    productKey: ProductKeyTypes;
    headerLinkText: string;
    headerLinkHref: string;
    desktopLinks: GlobalNavLinkContentProps[];
};

export type SubNavProps = {
    productKey: ProductKeyTypes;
    headerLinkText: string;
    headerLinkHref: string;
    desktopLinks: GlobalNavLinkContentProps[];
    mobileLinks: MobileNavLinksContainerInfo[];
};

export const SubNavNavigationBar = ({
    productKey,
    headerLinkText,
    headerLinkHref,
    desktopLinks
}: SubNavNavigationBarProps) => {
    const globalLinksRef = useRef<HTMLUListElement>(null);

    return (
        <div css={navigationBarWrapperCss} className="desktop-sub-nav">
            <div
                className="sub-navigation-bar"
                css={css({
                    display: "none",
                    alignItems: "center",
                    justifyContent: "center",
                    flex: 1,
                    paddingInline: "40px",
                    flexDirection: "row",
                    position: "relative",
                    zIndex: 4,
                    marginInline: "24px",

                    [media.above.md]: {
                        display: "flex",
                        maxWidth: "1152px",
                        paddingInline: "0",
                        height: "100%",
                        marginInline: "60px"
                    }
                })}
            >
                <nav
                    css={css({
                        display: "flex",
                        flexGrow: 1,
                        flexShrink: 1,
                        flexBasis: "0%",
                        height: "100%",
                        alignItems: "center"
                    })}
                >
                    <div
                        className="navigation-bar-logo"
                        css={navigationBarLogoCss}
                    >
                        <a
                            data-testid="sub-navigation-bar-logo-link"
                            css={textWrapperCss}
                            href={headerLinkHref}
                            aria-label={headerLinkText}
                            data-event="clicked"
                            data-event-component="linkButton"
                            data-event-container="subNavWac"
                            data-product-key={productKey}
                        >
                            <span css={textCss}>{headerLinkText}</span>
                        </a>
                    </div>
                    <GlobalNavLinksContainer
                        globalLinksProps={desktopLinks}
                        globalLinksRef={globalLinksRef}
                        isSubnav={true}
                    />
                </nav>
            </div>
        </div>
    );
};

export const SubNav = ({
    productKey,
    headerLinkText,
    headerLinkHref,
    desktopLinks,
    mobileLinks
}: SubNavProps) => {
    const globalNavItemKeys = useMemo(() => {
        return desktopLinks.map((link) => link.globalLinksKey);
    }, [desktopLinks]);

    return (
        <UFOSegment name="subnav">
            <div css={subNavCss}>
                <GlobalNavProvider globalNavItemKeys={globalNavItemKeys}>
                    <SubNavNavigationBar
                        productKey={productKey}
                        headerLinkText={headerLinkText}
                        headerLinkHref={headerLinkHref}
                        desktopLinks={desktopLinks}
                    />
                </GlobalNavProvider>
                <div className="mobile-nav" css={mobileNavCss}>
                    <MobileSubNav
                        productKey={productKey}
                        headerLinkText={headerLinkText}
                        headerLinkHref={headerLinkHref}
                        isShowSearchButton={false}
                        mobileLinks={mobileLinks}
                    />
                </div>
            </div>
        </UFOSegment>
    );
};

SubNav.displayName = "SubNav";
